/**
 * Xero payment transmission is separate from Portal "marked paid".
 * Reuses deal_invoices / portal_invoices columns:
 *   xeroPaymentSyncStatus: pending | synced | failed | skipped
 *   xeroPaymentSyncedAt, xeroPaymentSyncError, xeroPaymentId, xeroPaymentsJson
 *
 * Suggested product states → DB mapping:
 *   not_ready → unpaid (ignore status)
 *   ready     → paid + (null | pending)
 *   sending   → UI-only while request in flight
 *   sent      → synced
 *   failed    → failed (retryable)
 */
export type XeroPaymentTransmissionState =
  | "not_ready"
  | "ready"
  | "sent"
  | "failed"
  | "skipped";

export function resolveXeroPaymentTransmissionState(input: {
  invoicePaid: boolean;
  xeroPaymentSyncStatus?: string | null;
}): XeroPaymentTransmissionState {
  if (!input.invoicePaid) return "not_ready";
  const s = String(input.xeroPaymentSyncStatus ?? "")
    .trim()
    .toLowerCase();
  if (s === "synced") return "sent";
  if (s === "failed") return "failed";
  if (s === "skipped") return "skipped";
  // pending, empty, or legacy null after paid → ready for manual Send to Xero
  return "ready";
}

/** Finance/Admin may send when paid and not yet successfully synced. */
export function canSendXeroPayment(input: {
  invoicePaid: boolean;
  xeroPaymentSyncStatus?: string | null;
}): boolean {
  const state = resolveXeroPaymentTransmissionState(input);
  return state === "ready" || state === "failed";
}

export function isXeroPaymentSent(xeroPaymentSyncStatus?: string | null): boolean {
  return String(xeroPaymentSyncStatus ?? "").trim().toLowerCase() === "synced";
}

/** Status written when Portal marks paid without enqueueing Xero. */
export const XERO_PAYMENT_READY_STATUS = "pending" as const;
